// Capa de datos del módulo de TEMPERATURA.
// Perfil inicial: termómetros de contacto con dos series asc/desc.

import { supabase } from './supabase';
import {
  DEFAULT_TEMPERATURE_FAMILY_DATA,
  TEMPERATURE_CONTACT_TEMPLATE,
  buildTemperatureBlock,
  computeTemperaturePointMetrics,
  defaultTemperatureFamilyData,
  mergeTemperatureFamilyData,
  resolveTemperatureTemplate,
  suggestTemperatureSetpoints,
} from './temperature-calibration-profile';

export {
  getTodayDailyEnvironmentLog,
  upsertTodayDailyEnvironmentLog,
} from './api-pressure-manometer-pilot';

export const DEFAULT_VALIDITY_MONTHS = 12;
const VALIDITY_DAYS_BY_MONTHS = { 3: 90, 6: 181, 12: 365, 24: 730 };

function finiteOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function sanitizePatch(patch = {}) {
  return Object.fromEntries(
    Object.entries(patch).filter(([, value]) => value !== undefined && !(typeof value === 'number' && !Number.isFinite(value)))
  );
}

async function currentUserId() {
  return (await supabase.auth.getUser())?.data?.user?.id ?? null;
}

export function resolveTemperatureValidityDays(months = DEFAULT_VALIDITY_MONTHS) {
  const value = Number(months);
  if (!Number.isFinite(value) || value <= 0) return VALIDITY_DAYS_BY_MONTHS[DEFAULT_VALIDITY_MONTHS];
  return VALIDITY_DAYS_BY_MONTHS[value] ?? Math.round((365 / 12) * value);
}

export function addTemperatureValidityDaysToIsoDate(isoDate, months = DEFAULT_VALIDITY_MONTHS) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return null;
  const date = new Date(`${base}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + resolveTemperatureValidityDays(months));
  return date.toISOString().slice(0, 10);
}

async function getTodayEnvironment() {
  const { getTodayDailyEnvironmentLog } = await import('./api-pressure-manometer-pilot');
  try {
    return await getTodayDailyEnvironmentLog();
  } catch {
    return null;
  }
}

export async function createTemperatureSession({
  instrumentId,
  serviceOrderId = null,
  serviceOrderCode = null,
  certificateType = 'NO_SAC',
  validityMonths = DEFAULT_VALIDITY_MONTHS,
  templateCode = TEMPERATURE_CONTACT_TEMPLATE.code,
}) {
  if (!instrumentId) throw new Error('instrumentId es requerido');

  const { data: instrument, error: instrumentError } = await supabase
    .from('instruments')
    .select(`id, tag, lab_id, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance,
             client:client_id ( id, client_number, name, address, locality, province )`)
    .eq('id', instrumentId)
    .single();
  if (instrumentError) throw instrumentError;

  const dailyEnvironment = await getTodayEnvironment();
  const template = resolveTemperatureTemplate(templateCode);
  const familyData = mergeTemperatureFamilyData(defaultTemperatureFamilyData(template.code), template.code);
  const payload = {
    family: 'temperature',
    instrument_id: instrument.id,
    service_order_id: serviceOrderId,
    service_order_code: serviceOrderCode || null,
    certificate_type: certificateType,
    status: 'draft',
    template_code: template.code,
    template_revision: template.revision,

    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null,
    client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null,
    client_province: instrument?.client?.province || null,

    instrument_object: instrument?.type || template.objectFallback || 'Termómetro',
    instrument_brand: instrument?.brand || null,
    instrument_model: instrument?.model || null,
    instrument_serial: instrument?.serial || null,
    instrument_tag: instrument?.tag || null,
    instrument_unit: instrument?.unit || '°C',
    instrument_resolution: finiteOrNull(instrument?.resolution),
    instrument_range_min: finiteOrNull(instrument?.range_min),
    instrument_range_max: finiteOrNull(instrument?.range_max),
    instrument_class: finiteOrNull(instrument?.tolerance),

    standard_id: null,
    standard_name: null,
    standard_serial: null,
    standard_certificate: null,
    standard_calibration_date: null,

    ambient_pressure_avg: finiteOrNull(dailyEnvironment?.pressure_hpa),
    ambient_temp_avg: finiteOrNull(dailyEnvironment?.temp_c),
    ambient_humidity_avg: finiteOrNull(dailyEnvironment?.humidity_rh),

    validity_months: Number(validityMonths) || DEFAULT_VALIDITY_MONTHS,
    family_data: familyData,
    created_by: await currentUserId(),
  };

  const { data: session, error } = await supabase
    .from('calibration_sessions')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;

  const { error: blockError } = await supabase
    .from('calibration_session_blocks')
    .insert({ ...buildTemperatureBlock(template.code), session_id: session.id });
  if (blockError) throw blockError;

  return session;
}

export async function createTemperatureContactSession(options = {}) {
  return createTemperatureSession({
    ...options,
    templateCode: options.templateCode || TEMPERATURE_CONTACT_TEMPLATE.code,
  });
}

export async function listTemperatureStandardOptions({
  usageRole,
  onDate,
  rangeMin,
  rangeMax,
}) {
  const { data, error } = await supabase.rpc('list_temperature_standard_options', {
    p_usage_role: usageRole,
    p_on_date: onDate || new Date().toISOString().slice(0, 10),
    p_range_min: usageRole === 'environment' ? null : finiteOrNull(rangeMin),
    p_range_max: usageRole === 'environment' ? null : finiteOrNull(rangeMax),
  });
  if (error) throw error;
  return data || [];
}

export async function getTemperatureStandardConfiguration(sessionId) {
  const { data, error } = await supabase.rpc(
    'get_temperature_session_standard_configuration',
    { p_session_id: sessionId },
  );
  if (error) throw error;
  return data || {};
}

export async function validateTemperatureStandardConfiguration(sessionId) {
  const { data, error } = await supabase.rpc(
    'validate_temperature_session_standards',
    { p_session_id: sessionId },
  );
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar la trazabilidad.'], configuration: {} };
}

export async function validateTemperatureProfileData(sessionId) {
  const { data, error } = await supabase.rpc(
    'validate_temperature_profile_data',
    { p_session_id: sessionId },
  );
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar el perfil de temperatura.'] };
}

export async function configureTemperatureSessionStandards(sessionId, {
  sourceCapabilityId,
  referenceCapabilityId,
  environmentCapabilityId,
}) {
  const { data, error } = await supabase.rpc(
    'configure_temperature_session_standards',
    {
      p_session_id: sessionId,
      p_source_capability_id: sourceCapabilityId || null,
      p_reference_capability_id: referenceCapabilityId || null,
      p_environment_capability_id: environmentCapabilityId || null,
    },
  );
  if (error) throw error;
  return data;
}

export async function getTemperatureSession(sessionId) {
  const { data, error } = await supabase
    .from('calibration_sessions')
    .select('*')
    .eq('id', sessionId)
    .eq('family', 'temperature')
    .single();
  if (error) throw error;
  return data;
}

export async function updateTemperatureSession(sessionId, patch) {
  const normalized = sanitizePatch(patch);
  if (normalized.family_data) {
    const { data: current } = await supabase
      .from('calibration_sessions')
      .select('template_code')
      .eq('id', sessionId)
      .maybeSingle();
    normalized.family_data = mergeTemperatureFamilyData(normalized.family_data, current?.template_code);
  }
  const { data, error } = await supabase
    .from('calibration_sessions')
    .update({
      ...normalized,
      updated_at: new Date().toISOString(),
      updated_by: await currentUserId(),
    })
    .eq('id', sessionId)
    .eq('family', 'temperature')
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function listTemperatureSessionsByInstrument(instrumentId, templateCode = null) {
  let query = supabase
    .from('calibration_sessions')
    .select('*')
    .eq('instrument_id', instrumentId)
    .eq('family', 'temperature');
  if (templateCode) query = query.eq('template_code', templateCode);
  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function listTemperatureBlocks(sessionId) {
  const { data, error } = await supabase
    .from('calibration_session_blocks')
    .select('*')
    .eq('session_id', sessionId)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function listTemperaturePoints(sessionId) {
  const { data, error } = await supabase
    .from('calibration_session_points')
    .select('*')
    .eq('session_id', sessionId)
    .order('point_no', { ascending: true })
    .order('series_no', { ascending: true });
  if (error) throw error;
  return data || [];
}

export function temperaturePointsToRows(block, points = [], session = {}) {
  const pointCount = Number(block?.point_count) || TEMPERATURE_CONTACT_TEMPLATE.pointCount;
  const suggested = suggestTemperatureSetpoints(
    session.instrument_range_min,
    session.instrument_range_max,
    pointCount,
  );
  const rows = Array.from({ length: pointCount }, (_, index) => ({
    point_no: index + 1,
    setpoint: suggested[index] ?? '',
    s1_asc_pattern: '',
    s1_asc_instrument: '',
    s1_desc_pattern: '',
    s1_desc_instrument: '',
    s2_asc_pattern: '',
    s2_asc_instrument: '',
    s2_desc_pattern: '',
    s2_desc_instrument: '',
  }));

  const directions = {
    '1:ASC': ['s1_asc_pattern', 's1_asc_instrument'],
    '1:DESC': ['s1_desc_pattern', 's1_desc_instrument'],
    '2:ASC': ['s2_asc_pattern', 's2_asc_instrument'],
    '2:DESC': ['s2_desc_pattern', 's2_desc_instrument'],
  };
  for (const point of points) {
    const row = rows[Number(point.point_no) - 1];
    if (!row || (block?.id && point.block_id !== block.id)) continue;
    const keys = directions[`${Number(point.series_no)}:${String(point.direction || '').toUpperCase()}`];
    if (!keys) continue;
    row.setpoint = point.setpoint ?? row.setpoint;
    row[keys[0]] = point.pattern_reading ?? '';
    row[keys[1]] = point.instrument_reading ?? '';
  }
  return rows;
}

export async function saveTemperatureContactPoints(sessionId, block, rows = []) {
  if (!sessionId || !block?.id) throw new Error('Sesión y bloque requeridos');
  const payload = [];
  const mapping = [
    { series_no: 1, direction: 'ASC', pattern: 's1_asc_pattern', instrument: 's1_asc_instrument' },
    { series_no: 1, direction: 'DESC', pattern: 's1_desc_pattern', instrument: 's1_desc_instrument' },
    { series_no: 2, direction: 'ASC', pattern: 's2_asc_pattern', instrument: 's2_asc_instrument' },
    { series_no: 2, direction: 'DESC', pattern: 's2_desc_pattern', instrument: 's2_desc_instrument' },
  ];

  rows.forEach((row) => {
    const pointNo = Number(row.point_no);
    if (!Number.isFinite(pointNo)) return;
    const setpoint = finiteOrNull(row.setpoint);
    mapping.forEach((reading) => {
      const pattern = finiteOrNull(row[reading.pattern]);
      const instrument = finiteOrNull(row[reading.instrument]);
      if (pattern === null || instrument === null) return;
      payload.push({
        session_id: sessionId,
        block_id: block.id,
        point_no: pointNo,
        series_no: reading.series_no,
        direction: reading.direction,
        setpoint: setpoint ?? pattern,
        pattern_reading: pattern,
        instrument_reading: instrument,
        unit: '°C',
      });
    });
  });

  const { error: deleteError } = await supabase
    .from('calibration_session_points')
    .delete()
    .eq('session_id', sessionId)
    .eq('block_id', block.id);
  if (deleteError) throw deleteError;
  if (!payload.length) return [];

  const { data, error } = await supabase
    .from('calibration_session_points')
    .insert(payload)
    .select('*');
  if (error) throw error;
  return data || [];
}

export async function saveTemperaturePoints(sessionId, block, rows = []) {
  return saveTemperatureContactPoints(sessionId, block, rows);
}

export async function calculateTemperatureResults(sessionId) {
  const [validation, profileValidation] = await Promise.all([
    validateTemperatureStandardConfiguration(sessionId),
    validateTemperatureProfileData(sessionId),
  ]);
  if (!validation?.valid) {
    throw new Error(
      (validation?.issues || ['La configuración de patrones no es válida.']).join(' '),
    );
  }
  if (!profileValidation?.valid) {
    throw new Error(
      (profileValidation?.issues || ['La configuración específica del perfil no es válida.']).join(' '),
    );
  }
  const [session, blocks, points] = await Promise.all([
    getTemperatureSession(sessionId),
    listTemperatureBlocks(sessionId),
    listTemperaturePoints(sessionId),
  ]);
  const block = blocks.find((item) => item.block_key === 'temperature_contact') || blocks[0];
  if (!block) throw new Error('La sesión no tiene un bloque de temperatura.');
  const rows = temperaturePointsToRows(block, points, session);
  const results = rows
    .map((row) => {
      const metrics = computeTemperaturePointMetrics(row, session);
      if (metrics?.traceability_issue) {
        throw new Error(`Punto ${row.point_no}: ${metrics.traceability_issue}`);
      }
      return metrics
        ? { session_id: sessionId, block_id: block.id, point_no: row.point_no, ...metrics }
        : null;
    })
    .filter(Boolean);

  const { error: deleteError } = await supabase
    .from('calibration_session_results')
    .delete()
    .eq('session_id', sessionId);
  if (deleteError) throw deleteError;

  if (results.length) {
    const { error } = await supabase
      .from('calibration_session_results')
      .insert(results);
    if (error) throw error;
  }

  await updateTemperatureSession(sessionId, { status: 'calculated' });
  return results;
}

export async function listTemperatureResults(sessionId) {
  const { data, error } = await supabase
    .from('calibration_session_results')
    .select('*')
    .eq('session_id', sessionId)
    .order('point_no', { ascending: true });
  if (error) throw error;
  return data || [];
}
